/**
 * Web-side RBAC helpers for the Modules (Module 360) area.
 *
 * THIN ADAPTER over the single shared tier helper in `auth/tier.ts` — the same
 * pattern as `views/clients/rbac.ts` (`canWriteClients`/`isClientsAdmin`) and
 * `views/infrastructure/rbac.ts` (`canManageInfra`/`isInfraAdmin`). The whole
 * three-tier model (viewer / operator / admin) and the API-aligned
 * `currentUser.tier` resolution live in auth/tier.ts; this file only re-labels
 * the two gates the Modules views read:
 *   - canWriteModules  → operator OR admin (module-owner add/edit/remove,
 *                         module-scoped comms, DevOps link writes)
 *   - isModulesAdmin   → admin only (restore + includeDeleted reads — matches
 *                         the API's requireAdminForRestore /
 *                         requireAdminForIncludeDeleted gates)
 *
 * Note what is NOT gated here: the governed catalog identity and every
 * `module_governance` / `module_releases` row are SYNC-OWNED. No tier may edit
 * them — the API refuses external writes outright (`internalOnly()`), so there
 * is no UI gate to express.
 *
 * SECURITY: a green light here is never sufficient — the organization-domain
 * services re-check the effective tier on every write (requireWriteAccess);
 * this only governs whether the UI renders the controls.
 */
import type RootStore from "stores/RootStore";
import { isOperator, isAdmin } from "auth/tier";

/** operator OR admin — may run any Module 360 write the API operator-gates. */
export function canWriteModules(rootStore: RootStore): boolean {
  return isOperator(rootStore);
}

/** admin ONLY — restore retired catalog entries and read with `includeDeleted`. */
export function isModulesAdmin(rootStore: RootStore): boolean {
  return isAdmin(rootStore);
}
